// Rug Room Shots: the large side-by-side COMPARE view (SPEC 9.11).
//   * rug (silo) | result, or room | result, or rug | room | result, all panels the same size;
//   * synced zoom and pan (wheel about the cursor, drag, double-click, + / - / reset, Lock zoom);
//   * the 1200 px preview by default, "Load full 4K" on demand; the Large tier for rug and room;
//   * previous / next row of the Review tab's current filter, across page edges;
//   * Approve / Reject (reason, keys 1-8) / Regenerate (note, base room, detail photo, speed) / Clear without closing.
// It reads its rows from the Review tab's list, so the Review tab's polling keeps it current.
// 2026-10-21 build spec 9.3: earlier attempts (Review "Include earlier attempts") are view-only; "Wrong style" is
// disabled for sunroom shots (the number keys keep their meaning); the "Correct style" chips never offer Sunroom.
// Nothing is prefetched (CLAUDE.md: hidden prefetches slowed pages). Callbacks are declared
// before the effects that use them; no eslint-disable comments (SPEC 0.2 rule 8).
// AI check (SPEC_AICHECK 5.9): a strip under the toolbar with the AI chip and the reason, and an "AI details" switch
// that shows the compact AiCheckPanel in the side column (on by default for Fail / Needs a look; the person's choice
// then sticks while the view is open). Reject pre-selects the AI's first problem (the number keys are unchanged) and
// the Regenerate form offers "Use the AI's note". No new keyboard keys.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Dialog, Stack, Typography, IconButton, Button, Chip, Tooltip, Switch, FormControlLabel, Divider,
  TextField, CircularProgress, Checkbox, RadioGroup, Radio, Alert, Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  MdClose, MdChevronLeft, MdChevronRight, MdZoomIn, MdZoomOut, MdCenterFocusStrong, MdThumbUp, MdThumbDown,
  MdReplay, MdUndo, MdHd, MdViewColumn, MdLock, MdLockOpen, MdInfoOutline, MdKeyboard, MdBrokenImage, MdAutoFixHigh,
} from 'react-icons/md';
import rrsApi from './api';
import RoomPicker from './RoomPicker';
import GenerationDetailDialog from './GenerationDetailDialog';
import AiCheckPanel from './AiCheckPanel';
import DetailPhotoBlock, {
  DETAIL_OFF, detailBody, detailReady, DETAIL_SOURCE_WORDS,
} from './DetailPhotoBlock';
import {
  AI_COLOR, primaryButtonSx, GEN_STATUS, REVIEW_STATE, REJECT_REASON_LABELS, styleColor,
  fmtUsd, fmtRate, fmtInt, styleName, shapeName, errText, useNow, StyleChip, StatusChip, ImageLightbox,
  manualStyles, isEarlierAttempt, reasonBlocked, WRONG_STYLE_TWIN_TIP, SunroomShotChip,
  AiChip, aiWhyLine, aiSuggestedReason,
} from './common';
import { roomPickWarnings } from './roomRules';
import {
  SpeedChip, SpeedChooser, rateFor, defaultSpeed, resultStatusContent, regenWord, isBatch,
} from './speed';

// ---------------------------------------------------------------------------
// Synced zoom: one shared view {scale, cx, cy} (or one per panel when unlocked).
// scale 1..12; (cx, cy) = the normalised panel point (0..1) shown at the panel centre.
// Each panel applies translate(W/2 - cx*W*s, H/2 - cy*H*s) scale(s) with origin 0 0, so
// the same region is magnified everywhere. cx, cy are clamped so content never leaves.
// ---------------------------------------------------------------------------
const ZOOM_MIN = 1;
const ZOOM_MAX = 12;
const IDENTITY = { scale: 1, cx: 0.5, cy: 0.5 };
const SHARED = 'shared';
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const clampView = (v) => {
  const scale = clamp(Number(v.scale) || 1, ZOOM_MIN, ZOOM_MAX);
  const half = 0.5 / scale;
  return { scale, cx: clamp(v.cx, half, 1 - half), cy: clamp(v.cy, half, 1 - half) };
};
// Zoom about the content point under the panel point (u, v): c' = p + (c - p) * (s / s').
const zoomAbout = (view, u, v, nextScale) => {
  const s = view.scale;
  const s2 = clamp(nextScale, ZOOM_MIN, ZOOM_MAX);
  const px = view.cx + (u - 0.5) / s;
  const py = view.cy + (v - 0.5) / s;
  return clampView({ scale: s2, cx: px + (view.cx - px) * (s / s2), cy: py + (view.cy - py) * (s / s2) });
};

export const useSyncedZoom = (panelKeys) => {
  const [locked, setLockedState] = useState(true);
  const [views, setViews] = useState({});
  const keysRef = useRef(panelKeys);
  useEffect(() => { keysRef.current = panelKeys; }, [panelKeys]);

  const keyOf = useCallback((k) => (locked ? SHARED : k), [locked]);
  const viewOf = useCallback((k) => views[keyOf(k)] || IDENTITY, [views, keyOf]);

  const update = useCallback((k, fn) => {
    setViews((cur) => {
      const kk = locked ? SHARED : k;
      return { ...cur, [kk]: clampView(fn(cur[kk] || IDENTITY)) };
    });
  }, [locked]);

  // Every panel (or the shared view) at once: the toolbar buttons.
  const updateAll = useCallback((fn) => {
    setViews((cur) => {
      if (locked) return { ...cur, [SHARED]: clampView(fn(cur[SHARED] || IDENTITY)) };
      const next = { ...cur };
      (keysRef.current || []).forEach((k) => { next[k] = clampView(fn(cur[k] || IDENTITY)); });
      return next;
    });
  }, [locked]);

  const zoomAt = useCallback((k, u, v, factor) => update(k, (view) => zoomAbout(view, u, v, view.scale * factor)), [update]);
  const panBy = useCallback((k, dxPx, dyPx, w, h) => update(k, (view) => ({
    ...view,
    cx: view.cx - dxPx / (Math.max(1, w) * view.scale),
    cy: view.cy - dyPx / (Math.max(1, h) * view.scale),
  })), [update]);
  // Double-click: 1x <-> 3x at that point.
  const toggleAt = useCallback((k, u, v) => update(k, (view) => (view.scale > 1.01 ? IDENTITY : zoomAbout(view, u, v, 3))), [update]);
  const zoomBy = useCallback((factor) => updateAll((view) => zoomAbout(view, 0.5, 0.5, view.scale * factor)), [updateAll]);
  const reset = useCallback(() => setViews({}), []);

  // Locking adopts the result panel's view; unlocking copies the shared view to every panel.
  const setLocked = useCallback((next) => {
    setViews((cur) => {
      if (next) return { [SHARED]: cur.result || cur[SHARED] || IDENTITY };
      const shared = cur[SHARED] || IDENTITY;
      const out = {};
      (keysRef.current || []).forEach((k) => { out[k] = shared; });
      return out;
    });
    setLockedState(next);
  }, []);

  // The percent label shows the shared view, or the result panel's when unlocked.
  const headline = locked ? (views[SHARED] || IDENTITY) : (views.result || IDENTITY);

  return { locked, setLocked, viewOf, zoomAt, panBy, toggleAt, zoomBy, reset, headline };
};

// ---------------------------------------------------------------------------
// One zoomable panel. The wheel listener is attached with { passive: false } through a
// ref, because React's onWheel is passive and cannot preventDefault (page scroll).
// ---------------------------------------------------------------------------
const wheelPixels = (e, h) => {
  let dy = e.deltaY;
  if (e.deltaMode === 1) dy *= 16; // lines
  else if (e.deltaMode === 2) dy *= h || 800; // pages
  return clamp(dy, -400, 400);
};

const ZoomPanel = ({
  panelKey, label, sub, src, hiSrc, hiLabel, view, onZoomAt, onPan, onToggleAt, placeholder, placeholderColor,
  onImgLoad,
}) => {
  const theme = useTheme();
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [broken, setBroken] = useState(false);
  const [hiLoaded, setHiLoaded] = useState(false);
  const [hiBroken, setHiBroken] = useState(false);
  const [dragging, setDragging] = useState(false);
  const showHi = !!hiSrc && hiSrc !== src;

  useEffect(() => { setBroken(false); }, [src]);
  useEffect(() => { setHiLoaded(false); setHiBroken(false); }, [hiSrc]);

  // Latest handler for the native wheel listener (attached once per element). A panel
  // without an image (status text) does not zoom, so the wheel scrolls nothing there.
  const onZoomRef = useRef(onZoomAt);
  const interactiveRef = useRef(!placeholder);
  useEffect(() => { onZoomRef.current = onZoomAt; }, [onZoomAt]);
  useEffect(() => { interactiveRef.current = !placeholder; }, [placeholder]);

  const handleWheel = useCallback((e) => {
    const el = boxRef.current;
    if (!el) return;
    e.preventDefault();
    if (!interactiveRef.current) return;
    const r = el.getBoundingClientRect();
    const u = clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1);
    const v = clamp((e.clientY - r.top) / Math.max(1, r.height), 0, 1);
    const factor = 1.0015 ** (-wheelPixels(e, r.height));
    if (onZoomRef.current) onZoomRef.current(panelKey, u, v, factor);
  }, [panelKey]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const el = boxRef.current;
    if (!el) return;
    try { el.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!dx && !dy) return;
    d.x = e.clientX;
    d.y = e.clientY;
    const r = boxRef.current.getBoundingClientRect();
    onPan(panelKey, dx, dy, r.width, r.height);
  };
  const endDrag = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    try { boxRef.current.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
  };
  const onDoubleClick = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    onToggleAt(panelKey, clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1));
  };

  const s = view.scale;
  const transform = `translate(${(0.5 - view.cx * s) * 100}%, ${(0.5 - view.cy * s) * 100}%) scale(${s})`;
  const dark = theme.palette.mode === 'dark';
  const imgSx = {
    position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block',
    userSelect: 'none', pointerEvents: 'none',
  };

  return (
    <Box
      sx={{
        flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden', borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`, bgcolor: dark ? '#1b1b1b' : '#f2f0ec',
      }}
    >
      <Box
        ref={boxRef}
        onPointerDown={placeholder ? undefined : onPointerDown}
        onPointerMove={placeholder ? undefined : onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={placeholder ? undefined : onDoubleClick}
        sx={{
          position: 'absolute', inset: 0, touchAction: 'none',
          cursor: placeholder ? 'default' : (dragging ? 'grabbing' : (s > 1.01 ? 'grab' : 'zoom-in')),
        }}
      >
        {placeholder ? (
          <Box
            sx={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', textAlign: 'center', p: 3, color: placeholderColor,
            }}
          >
            {placeholder}
          </Box>
        ) : (
          // The transform is an inline style, not sx: a generated CSS class per zoom / pan step
          // would pile up rules in the page's stylesheet.
          <Box sx={{ position: 'absolute', inset: 0 }} style={{ transform, transformOrigin: '0 0' }}>
            {src && !broken ? (
              <Box
                component="img" src={src} alt={label} draggable={false}
                onLoad={onImgLoad} onError={() => setBroken(true)} sx={imgSx}
              />
            ) : (
              <Box sx={{ ...imgSx, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.35 }}>
                <MdBrokenImage size={48} />
              </Box>
            )}
            {showHi && !hiBroken && (
              <Box
                component="img" src={hiSrc} alt={`${label} full size`} draggable={false}
                onLoad={() => setHiLoaded(true)} onError={() => setHiBroken(true)}
                sx={{ ...imgSx, opacity: hiLoaded ? 1 : 0 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Label (top left) */}
      <Box
        sx={{
          position: 'absolute', top: 8, left: 8, px: 1, py: 0.25, borderRadius: 1, pointerEvents: 'none',
          bgcolor: dark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.85)', border: `1px solid ${theme.palette.divider}`,
          display: 'flex', alignItems: 'baseline', gap: 1, maxWidth: 'calc(100% - 16px)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
        {sub ? <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>{sub}</Typography> : null}
      </Box>

      {/* Full-size loading / failure (top right) */}
      {showHi && (!hiLoaded || hiBroken) && (
        <Box
          sx={{
            position: 'absolute', top: 8, right: 8, px: 1, py: 0.5, borderRadius: 1, pointerEvents: 'none',
            bgcolor: dark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)', border: `1px solid ${theme.palette.divider}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}
        >
          {hiBroken ? (
            <Typography variant="caption" color="error.main">The full-size image could not be loaded.</Typography>
          ) : (
            <>
              <CircularProgress size={14} />
              <Typography variant="caption">{hiLabel || 'Loading full size...'}</Typography>
            </>
          )}
        </Box>
      )}
      {showHi && hiLoaded && !hiBroken && (
        <Chip
          size="small" label="full size" color="success"
          sx={{ position: 'absolute', top: 8, right: 8, height: 20, fontSize: '0.65rem', pointerEvents: 'none' }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Text entry only: checkboxes, radios and switches keep the shortcuts working.
const isTextEntry = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'textarea' || tag === 'select' || el.isContentEditable) return true;
  if (tag !== 'input') return false;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'reset'].includes(type);
};

const hasResult = (it) => !!(it && it.status === 'succeeded' && it.result && (it.result.previewUrl || it.result.thumbUrl));

// GET /generations/{id} (camelCased columns + siloUrls / baseUrls / result) -> a review item.
const itemFromDetail = (g) => ({
  ...g,
  silo: {
    imageKey: g.siloImageKey,
    smallUrl: g.siloUrls && g.siloUrls.smallUrl,
    largeUrl: g.siloUrls && g.siloUrls.largeUrl,
    originalUrl: g.siloUrls && g.siloUrls.originalUrl,
  },
  base: {
    imageKey: g.baseImageKey,
    smallUrl: g.baseUrls && g.baseUrls.smallUrl,
    largeUrl: g.baseUrls && g.baseUrls.largeUrl,
    originalUrl: g.baseUrls && g.baseUrls.originalUrl,
  },
  previousAttempts: [],
  fromDetail: true,
});

const PANEL_MODES = [
  { key: 'rug-result', panels: ['silo', 'result'], next: 'Show room' },
  { key: 'room-result', panels: ['base', 'result'], next: 'Show rug and room' },
  { key: 'all', panels: ['silo', 'base', 'result'], next: 'Hide room' },
];

const KEY_HELP = [
  ['← / →  or  K / J', 'previous / next row'],
  ['A', 'approve'],
  ['R', 'reject (then 1-8 picks a reason, Enter submits)'],
  ['G', 'regenerate / retry (Ctrl+Enter submits)'],
  ['B', 'cycle rug | result → room | result → all three'],
  ['F', 'load the full 4K result'],
  ['Shift+F', 'full-size rug and room'],
  ['+ / - / 0', 'zoom in / out / reset (or wheel, drag, double-click)'],
  ['L', 'lock or unlock zoom between panels'],
  ['Esc', 'close the form, then the view'],
];

// ===========================================================================
const CompareDialog = ({
  genId, items, itemsPage, page, pageSize, total, ready, loading, stateFilter,
  config, speeds, jobSpeed, job, busyGenIds,
  onNavigate, onPageChange, onClose, onDecide, onRegenerate, notify,
  aiEnabled, onAiCheck, onRefresh,
}) => {
  const theme = useTheme();
  const narrow = useMediaQuery('(max-width:899.95px)');
  const styles = config.styles;
  // The merlot tool colour is too dark on a dark background; use a light tint there.
  const accent = theme.palette.mode === 'dark' ? '#E3A6AE' : AI_COLOR;

  const [snap, setSnap] = useState(null);
  const [modeIdx, setModeIdx] = useState(0);
  // The full-size flags belong to ONE row (the genId they were turned on for), so the first
  // render of the next row is already back on the light images: no 20-30 MB download is
  // started for a row and thrown away when an effect resets it (hidden-prefetch lesson).
  const [fullResultFor, setFullResultFor] = useState(null);
  const [fullSourcesFor, setFullSourcesFor] = useState(null);
  const fullResult = fullResultFor !== null && fullResultFor === genId;
  const fullSources = fullSourcesFor !== null && fullSourcesFor === genId;
  const toggleFullResult = useCallback(() => setFullResultFor((v) => (v === genId ? null : genId)), [genId]);
  const toggleFullSources = useCallback(() => setFullSourcesFor((v) => (v === genId ? null : genId)), [genId]);
  // genId whose result image has actually been drawn; Approve waits for it, so a decision
  // is never made on a row whose image has not appeared yet.
  const [resultShownFor, setResultShownFor] = useState(null);
  const [skipEmpty, setSkipEmpty] = useState(stateFilter === 'pending');
  const [nextAfter, setNextAfter] = useState(true);
  const [form, setForm] = useState(null); // null | 'reject' | 'regen'
  const [showKeys, setShowKeys] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [rejectStyle, setRejectStyle] = useState('');
  const [rejectSuggested, setRejectSuggested] = useState(null); // the reason the AI check suggested, if any
  // AI details: null = automatic (shown for Fail / Needs a look), true / false = the person's choice.
  const [aiPref, setAiPref] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [regenNote, setRegenNote] = useState('');
  const [regenRoomMode, setRegenRoomMode] = useState('same');
  const [regenRoom, setRegenRoom] = useState('');
  const [regenInPrompt, setRegenInPrompt] = useState(false);
  const [regenSpeed, setRegenSpeed] = useState('fast');
  const [regenRoomObj, setRegenRoomObj] = useState(null);
  const [regenRooms, setRegenRooms] = useState([]);
  const [regenDetail, setRegenDetail] = useState(DETAIL_OFF);
  const [roomPreview, setRoomPreview] = useState(null);
  const [formBusy, setFormBusy] = useState(false);

  const lastIndex = useRef(0);
  const pendingEdge = useRef(null); // { page, edge: 'first' | 'last' }
  const detailFetched = useRef(null);
  const rejectBoxRef = useRef(null);

  // ---- which row -------------------------------------------------------------
  const exactIdx = useMemo(() => items.findIndex((it) => it.genId === genId), [items, genId]);
  // A newer attempt of the same rug shape replaced this row (after Regenerate).
  const targetIdx = useMemo(() => {
    if (exactIdx >= 0 || !snap || snap.genId !== genId) return -1;
    return items.findIndex((it) => it.targetId === snap.targetId && it.genId !== genId);
  }, [items, exactIdx, snap, genId]);
  const cur = exactIdx >= 0 ? items[exactIdx] : (snap && snap.genId === genId ? snap : null);

  useEffect(() => {
    if (exactIdx >= 0) {
      lastIndex.current = exactIdx;
      setSnap(items[exactIdx]);
    }
  }, [exactIdx, items]);

  // Follow the newer attempt of the same target (Regenerate without "next").
  useEffect(() => {
    if (targetIdx >= 0) onNavigate(items[targetIdx].genId);
  }, [targetIdx, items, onNavigate]);

  // Opened from a link to a row that is not on this page / filter: load it on its own.
  useEffect(() => {
    if (!ready || loading || exactIdx >= 0 || targetIdx >= 0) return;
    if (snap && snap.genId === genId) return;
    if (detailFetched.current === genId) return;
    detailFetched.current = genId;
    rrsApi.getGeneration(genId)
      .then((g) => { if (g && g.genId) setSnap(itemFromDetail(g)); })
      .catch((e) => notify(`Generation ${genId}: ${errText(e)}`, 'error'));
  }, [ready, loading, exactIdx, targetIdx, snap, genId, notify]);

  // ---- zoom --------------------------------------------------------------------
  const mode = PANEL_MODES[modeIdx];
  const zoom = useSyncedZoom(mode.panels);
  const {
    reset: resetZoom, zoomBy, locked: zoomLocked, setLocked: setZoomLocked,
  } = zoom;

  // A new row starts at fit and with no open form (the light images follow from fullResultFor).
  useEffect(() => {
    resetZoom();
    setForm(null);
  }, [genId, resetZoom]);

  // ---- navigation ---------------------------------------------------------------
  const eligible = useCallback((it) => !skipEmpty || hasResult(it), [skipEmpty]);

  const pickOnPage = useCallback((edge) => {
    const order = edge === 'first' ? items : [...items].reverse();
    return order.find(eligible) || null;
  }, [items, eligible]);

  const go = useCallback((dir) => {
    let i;
    if (exactIdx >= 0) i = exactIdx + dir;
    else if (cur && cur.fromDetail) i = dir > 0 ? 0 : -1;
    else i = dir > 0 ? lastIndex.current : lastIndex.current - 1; // the row slid out of the filter
    while (i >= 0 && i < items.length && !eligible(items[i])) i += dir;
    if (i >= 0 && i < items.length) { onNavigate(items[i].genId); return; }
    if (dir > 0 && (page + 1) * pageSize < total) {
      pendingEdge.current = { page: page + 1, edge: 'first' };
      onPageChange(page + 1);
    } else if (dir < 0 && page > 0) {
      pendingEdge.current = { page: page - 1, edge: 'last' };
      onPageChange(page - 1);
    } else {
      notify(dir > 0 ? 'That was the last row of this filter.' : 'This is the first row of this filter.', 'info');
    }
  }, [exactIdx, cur, items, eligible, onNavigate, page, pageSize, total, onPageChange, notify]);

  // After a page-edge move, continue on the first / last row of the new page.
  useEffect(() => {
    const pe = pendingEdge.current;
    if (!pe || loading || itemsPage !== pe.page) return;
    const hit = pickOnPage(pe.edge);
    if (hit) {
      pendingEdge.current = null;
      onNavigate(hit.genId);
      return;
    }
    // Nothing eligible on this page: keep going in the same direction, or stop.
    if (pe.edge === 'first' && (pe.page + 1) * pageSize < total) {
      pendingEdge.current = { page: pe.page + 1, edge: 'first' };
      onPageChange(pe.page + 1);
    } else if (pe.edge === 'last' && pe.page > 0) {
      pendingEdge.current = { page: pe.page - 1, edge: 'last' };
      onPageChange(pe.page - 1);
    } else {
      pendingEdge.current = null;
      notify('No more rows with a result in this filter.', 'info');
    }
  }, [itemsPage, loading, pickOnPage, onNavigate, onPageChange, pageSize, total, notify]);

  // The row on screen NOW and the latest go(), for code that resumes after an await: an
  // auto-advance must start from where the reviewer is, and only if they are still on the
  // row that was decided.
  const shownGenRef = useRef(genId);
  const goRef = useRef(go);
  useEffect(() => { shownGenRef.current = genId; }, [genId]);
  useEffect(() => { goRef.current = go; }, [go]);
  const advanceFrom = useCallback((decidedGenId) => {
    if (shownGenRef.current === decidedGenId) goRef.current(1);
  }, []);

  // ---- decisions ----------------------------------------------------------------
  const busy = !!cur && !!busyGenIds && busyGenIds.has(cur.genId);
  const resultShown = !!cur && resultShownFor === cur.genId;
  // An earlier attempt (Review "Include earlier attempts") is view-only.
  const earlierRow = isEarlierAttempt(cur);
  const succeeded = !!cur && cur.status === 'succeeded';
  const canDecide = succeeded && !earlierRow;
  const canRegen = !!cur && !earlierRow && ['succeeded', 'failed', 'unknown', 'cancelled'].includes(cur.status) && !cur.supersededByGenId;
  const smallMax = config.roomRules && config.roomRules.effective ? config.roomRules.effective.round_small_max : undefined;

  const mergeSnap = useCallback((genIdDone, patch) => {
    if (!patch || typeof patch !== 'object') return;
    setSnap((s) => (s && s.genId === genIdDone ? { ...s, ...patch } : s));
  }, []);

  const approve = useCallback(async () => {
    if (!cur || cur.status !== 'succeeded' || isEarlierAttempt(cur) || busy || !resultShown) return;
    const decided = cur.genId;
    const patch = await onDecide(cur, { decision: 'approve' });
    if (!patch) return;
    mergeSnap(decided, patch);
    if (nextAfter) advanceFrom(decided);
  }, [cur, busy, resultShown, onDecide, mergeSnap, nextAfter, advanceFrom]);

  const clearDecision = useCallback(async () => {
    if (!cur || cur.status !== 'succeeded' || isEarlierAttempt(cur) || busy) return;
    const patch = await onDecide(cur, { decision: 'clear' });
    if (patch) mergeSnap(cur.genId, patch);
  }, [cur, busy, onDecide, mergeSnap]);

  const openReject = useCallback(() => {
    if (!cur || cur.status !== 'succeeded' || isEarlierAttempt(cur)) return;
    // The AI's first problem is pre-selected when the AI failed or flagged the image and it has no reason yet.
    const suggested = aiSuggestedReason(cur, config.rejectReasons);
    setRejectReason(cur.reviewReasonCode || suggested || '');
    setRejectSuggested(suggested);
    setRejectNote(cur.reviewNote || '');
    setRejectStyle('');
    setForm('reject');
  }, [cur, config.rejectReasons]);

  const submitReject = useCallback(async () => {
    if (!cur || !rejectReason || formBusy) return;
    if (reasonBlocked(cur, rejectReason)) return;
    if (rejectReason === 'wrong_style' && !rejectStyle) return;
    const decided = cur.genId;
    setFormBusy(true);
    const patch = await onDecide(cur, {
      decision: 'reject', reasonCode: rejectReason, note: rejectNote.trim(),
      correctStyle: rejectReason === 'wrong_style' ? rejectStyle : undefined,
    });
    setFormBusy(false);
    if (!patch) return;
    mergeSnap(decided, patch);
    if (rejectReason === 'wrong_style') notify(`Style corrected to ${rejectStyle}. Regenerate to place it in a ${rejectStyle} room.`, 'info');
    if (shownGenRef.current !== decided) return; // moved on meanwhile: leave the current row alone
    setForm(null);
    if (nextAfter) advanceFrom(decided);
  }, [cur, rejectReason, rejectNote, rejectStyle, formBusy, onDecide, mergeSnap, notify, nextAfter, advanceFrom]);

  const openRegen = useCallback(() => {
    if (!canRegen) return;
    const styleChanged = cur.targetStyleCode && cur.targetStyleCode !== cur.styleCode;
    setRegenNote(cur.status === 'failed' || cur.status === 'unknown' ? 'Retry after failure' : '');
    setRegenRoomMode(styleChanged ? 'other' : 'same');
    setRegenRoom('');
    setRegenRoomObj(null);
    setRegenRooms([]);
    setRegenDetail(DETAIL_OFF);
    setRegenInPrompt(false);
    setRegenSpeed(defaultSpeed(jobSpeed || cur.speed, speeds));
    setForm('regen');
    if (styleChanged) {
      // Least-used room of the new style, of the right type for this rug (targetId: its room letters).
      rrsApi.suggestRoom(cur.targetStyleCode, cur.shapeCode, cur.roomCode, cur.targetId)
        .then((res) => { if (res && res.roomCode) setRegenRoom((r) => r || res.roomCode); })
        .catch(() => {});
    }
  }, [canRegen, cur, jobSpeed, speeds]);

  // "Use as regenerate note" (AI details): the Regenerate form with the AI's note, added to the prompt.
  const openRegenWithNote = useCallback((note) => {
    if (!canRegen) return;
    openRegen();
    setRegenNote(String(note || '').slice(0, 1000));
    setRegenInPrompt(true);
  }, [canRegen, openRegen]);

  // Check again (AI details): the Review tab queues it, reloads its list and returns the row patch.
  const checkAgain = useCallback(async () => {
    if (!cur || !onAiCheck || aiBusy) return;
    const target = cur;
    setAiBusy(true);
    const patch = await onAiCheck(target);
    setAiBusy(false);
    if (patch) mergeSnap(target.genId, patch);
  }, [cur, onAiCheck, aiBusy, mergeSnap]);

  const onRegenRoomsLoaded = useCallback((rooms) => {
    setRegenRooms(rooms || []);
    setRegenRoom((r) => {
      if (r || !cur) return r;
      const wanted = cur.targetStyleCode || cur.styleCode;
      const first = rooms.find((x) => x.styleCode === wanted && x.roomCode !== cur.roomCode)
        || rooms.find((x) => x.roomCode !== cur.roomCode);
      return first ? first.roomCode : r;
    });
  }, [cur]);

  const regenValid = regenNote.trim().length >= 3 && (regenRoomMode !== 'other' || !!regenRoom) && detailReady(regenDetail);
  const submitRegen = useCallback(async () => {
    if (!cur || !regenValid || formBusy) return;
    const decided = cur.genId;
    setFormBusy(true);
    const ok = await onRegenerate(cur, {
      note: regenNote.trim(),
      roomCode: regenRoomMode === 'other' ? regenRoom : null,
      noteInPrompt: regenInPrompt,
      speed: regenSpeed,
      ...detailBody(regenDetail),
    });
    setFormBusy(false);
    if (!ok) return;
    if (shownGenRef.current !== decided) return; // moved on meanwhile: leave the current row alone
    setForm(null);
    if (nextAfter) advanceFrom(decided);
  }, [cur, regenValid, formBusy, onRegenerate, regenNote, regenRoomMode, regenRoom, regenInPrompt, regenSpeed, regenDetail, nextAfter, advanceFrom]);

  const cycleMode = useCallback(() => setModeIdx((m) => (m + 1) % PANEL_MODES.length), []);

  // ---- keyboard -------------------------------------------------------------------
  const onKeyDown = useCallback((e) => {
    if (detailOpen || roomPreview || e.ctrlKey || e.metaKey || e.altKey) return;
    if (form || isTextEntry(e.target)) return;
    const k = e.key;
    const lk = k.length === 1 ? k.toLowerCase() : k;
    let handled = true;
    if (k === 'ArrowRight' || lk === 'j') go(1);
    else if (k === 'ArrowLeft' || lk === 'k') go(-1);
    else if (lk === 'a') approve();
    else if (lk === 'r') openReject();
    else if (lk === 'g') openRegen();
    else if (lk === 'b') cycleMode();
    else if (k === 'F') toggleFullSources();
    else if (k === 'f') toggleFullResult();
    else if (k === '+' || k === '=') zoomBy(1.5);
    else if (k === '-' || k === '_') zoomBy(1 / 1.5);
    else if (k === '0') resetZoom();
    else if (lk === 'l') setZoomLocked(!zoomLocked);
    else handled = false;
    if (handled) e.preventDefault();
  }, [detailOpen, roomPreview, form, go, approve, openReject, openRegen, cycleMode, toggleFullSources, toggleFullResult, zoomBy,
    resetZoom, setZoomLocked, zoomLocked]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  // Focus the reject panel so 1-7 and Enter work straight away.
  useEffect(() => {
    if (form === 'reject' && rejectBoxRef.current) rejectBoxRef.current.focus();
  }, [form]);

  const onRejectKeyDown = (e) => {
    const target = e.target;
    const typing = isTextEntry(target);
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      const role = target && target.getAttribute ? target.getAttribute('role') : null;
      if (target && (target.tagName === 'BUTTON' || role === 'button' || role === 'option')) return;
      e.preventDefault();
      submitReject();
      return;
    }
    if (!typing && /^[1-9]$/.test(e.key)) {
      // By position: 1-7 as before, 8 = Rug binding. Wrong style stays at 6 but cannot be picked for a sunroom shot.
      const r = (config.rejectReasons || [])[Number(e.key) - 1];
      if (r && !reasonBlocked(cur, r)) { setRejectReason(r); e.preventDefault(); }
    }
  };

  const onRegenKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submitRegen(); }
  };

  const handleClose = (event, reason) => {
    if (roomPreview) return; // the large room view closes first (its own dialog)
    if (reason === 'escapeKeyDown' && form) {
      if (!formBusy) setForm(null);
      return;
    }
    if (formBusy) return;
    onClose();
  };

  // ---- render pieces ------------------------------------------------------------------
  const now = useNow(!!cur && ['queued', 'running', 'batched', 'submitted'].includes(cur.status),
    cur && (isBatch(cur.speed) || ['batched', 'submitted'].includes(cur.status)) ? 30000 : 1000);

  const position = exactIdx >= 0 ? `${fmtInt(itemsPage * pageSize + exactIdx + 1)} / ${fmtInt(total)}` : `- / ${fmtInt(total)}`;

  const panelFor = (key) => {
    if (!cur) return null;
    const silo = cur.silo || {};
    const base = cur.base || {};
    const res = cur.result || {};
    const common = {
      panelKey: key, view: zoom.viewOf(key), onZoomAt: zoom.zoomAt, onPan: zoom.panBy, onToggleAt: zoom.toggleAt,
    };
    // Keyed by row as well as panel: each row mounts fresh <img> elements, so the previous
    // row's image is never shown under this row's header and buttons while the new one loads.
    const rowKey = `${key}-${cur.genId}`;
    if (key === 'silo') {
      const light = silo.largeUrl || silo.originalUrl || silo.smallUrl;
      return (
        <ZoomPanel
          key={rowKey} {...common} label="Rug (silo)" sub={cur.itemId} src={light}
          hiSrc={fullSources ? silo.originalUrl : null} hiLabel="Loading the full-size rug photo..."
        />
      );
    }
    if (key === 'base') {
      const light = base.largeUrl || base.originalUrl || base.smallUrl;
      return (
        <ZoomPanel
          key={rowKey} {...common} label="Room (base)" sub={cur.roomCode} src={light}
          hiSrc={fullSources ? base.originalUrl : null} hiLabel="Loading the full-size room photo..."
        />
      );
    }
    if (hasResult(cur)) {
      return (
        <ZoomPanel
          key={rowKey} {...common} label="Result" sub={res.outW ? `${res.outW}x${res.outH}${fullResult ? '' : ' · 1200 px preview'}` : null}
          src={res.previewUrl || res.thumbUrl} onImgLoad={() => setResultShownFor(cur.genId)}
          hiSrc={fullResult ? res.originalUrl : null} hiLabel="Loading the full 4K result (about 20-30 MB)..."
        />
      );
    }
    const st = resultStatusContent(cur, now, true);
    return <ZoomPanel key={rowKey} {...common} label="Result" placeholder={st.content} placeholderColor={st.color} />;
  };

  const rejectReasons = config.rejectReasons || [];
  const formOpen = !!form;
  // AI check of the row on screen (undefined from an older backend: no AI strip at all).
  const ai = cur ? cur.aiCheck : null;
  const aiAuto = !!ai && (ai.state === 'fail' || ai.state === 'look');
  const aiDetailsShown = !!ai && (aiPref === null ? aiAuto : aiPref);
  const sideOpen = !!cur && (formOpen || aiDetailsShown);
  const aiLine = ai ? aiWhyLine(ai) : '';
  const regenPrice = rateFor(regenSpeed, speeds, job);
  const regenChosenRoom = regenRoomMode === 'other' && regenRoom
    ? (regenRoomObj && regenRoomObj.roomCode === regenRoom ? regenRoomObj : regenRooms.find((r) => r.roomCode === regenRoom) || null)
    : null;
  const regenWarnings = cur && form === 'regen' ? roomPickWarnings(cur, regenChosenRoom, smallMax, config.roomLetters) : [];

  return (
    <Dialog
      open fullScreen onClose={handleClose}
      PaperProps={{ sx: { bgcolor: 'background.default', backgroundImage: 'none' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* Header */}
        <Stack
          direction="row" spacing={1} alignItems="center"
          sx={{ px: { xs: 1, sm: 2 }, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', rowGap: 0.75 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', mr: 0.5 }}>{position}</Typography>
          {cur ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{cur.itemId}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{cur.webId}</Typography>
              <Tooltip title={shapeName(cur.shapeCode, config.shapes)}><Chip size="small" variant="outlined" label={cur.shapeCode} /></Tooltip>
              <StyleChip code={cur.styleCode} styles={styles} />
              {cur.scene === 'sun' && <SunroomShotChip />}
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cur.roomCode}</Typography>
              <Chip size="small" variant="outlined" label={`attempt ${cur.attempt}`} />
              <SpeedChip speed={cur.speed || 'fast'} />
              <StatusChip map={GEN_STATUS} status={cur.status} />
              {succeeded && <StatusChip map={REVIEW_STATE} status={cur.reviewState} />}
              {cur.reviewReasonCode && <Chip size="small" color="error" variant="outlined" label={REJECT_REASON_LABELS[cur.reviewReasonCode] || cur.reviewReasonCode} />}
              {cur.detail && (
                <Tooltip title={`Detail photo sent: ${cur.detail.imageKey || ''}${cur.detail.source ? ` (${DETAIL_SOURCE_WORDS[cur.detail.source] || cur.detail.source})` : ''}`}>
                  <Chip size="small" color="info" variant="outlined" label="Detail photo sent" />
                </Tooltip>
              )}
              {earlierRow && (
                <Tooltip title="An earlier attempt: view only. Its rug's current image is the latest attempt.">
                  <Chip size="small" variant="outlined" label={`earlier attempt · current is gen ${cur.targetLatestGenId || cur.supersededByGenId || '?'}`} />
                </Tooltip>
              )}
              {cur.costUsd !== null && cur.costUsd !== undefined && (
                <Typography variant="body2" color="text.secondary">{fmtUsd(cur.costUsd, 3)}</Typography>
              )}
              {cur.fromDetail && (
                <Tooltip title="This row is not on the current page or filter of the Review tab; previous / next continue from the list.">
                  <Chip size="small" color="warning" variant="outlined" label="opened from a link" />
                </Tooltip>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">{loading || !ready ? 'Loading...' : `Generation ${genId}`}</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {busy && <CircularProgress size={18} />}
          <Tooltip title="Details and event trail">
            <span><IconButton size="small" onClick={() => setDetailOpen(true)} disabled={!cur}><MdInfoOutline /></IconButton></span>
          </Tooltip>
          <Tooltip title="Close (Esc)"><IconButton onClick={() => handleClose(null, 'button')} aria-label="Close"><MdClose /></IconButton></Tooltip>
        </Stack>

        {/* Toolbar */}
        <Stack
          direction="row" spacing={1} alignItems="center"
          sx={{ px: { xs: 1, sm: 2 }, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', rowGap: 0.75 }}
        >
          <Tooltip title="Previous row (← or K)"><span><IconButton size="small" onClick={() => go(-1)} disabled={formBusy}><MdChevronLeft /></IconButton></span></Tooltip>
          <Tooltip title="Next row (→ or J)"><span><IconButton size="small" onClick={() => go(1)} disabled={formBusy}><MdChevronRight /></IconButton></span></Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Cycle the panels (B)">
            <Button size="small" startIcon={<MdViewColumn />} onClick={cycleMode}>{mode.next}</Button>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Zoom out (-)"><IconButton size="small" onClick={() => zoom.zoomBy(1 / 1.5)}><MdZoomOut /></IconButton></Tooltip>
          <Typography variant="body2" sx={{ width: 52, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(zoom.headline.scale * 100)}%
          </Typography>
          <Tooltip title="Zoom in (+)"><IconButton size="small" onClick={() => zoom.zoomBy(1.5)}><MdZoomIn /></IconButton></Tooltip>
          <Tooltip title="Reset to fit (0)">
            <Button size="small" startIcon={<MdCenterFocusStrong />} onClick={zoom.reset}>Reset</Button>
          </Tooltip>
          <Tooltip title="On: every panel zooms and pans together (L)">
            <FormControlLabel
              sx={{ ml: 0, mr: 0.5 }}
              control={<Switch size="small" checked={zoom.locked} onChange={(e) => zoom.setLocked(e.target.checked)} />}
              label={(
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {zoom.locked ? <MdLock size={14} /> : <MdLockOpen size={14} />}
                  <Typography variant="body2">Lock zoom</Typography>
                </Stack>
              )}
            />
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={fullResult ? 'Back to the 1200 px preview (F)' : 'Load the full 4K result, about 20-30 MB (F)'}>
            <span>
              <Button
                size="small" startIcon={<MdHd />} variant={fullResult ? 'contained' : 'outlined'}
                onClick={toggleFullResult} disabled={!hasResult(cur)}
                sx={fullResult ? primaryButtonSx : { color: accent, borderColor: `${accent}88` }}
              >
                {fullResult ? 'Full 4K on' : 'Load full 4K'}
              </Button>
            </span>
          </Tooltip>
          {!fullResult && <Typography variant="caption" color="text.secondary">about 20-30 MB</Typography>}
          <Tooltip title="Use the original rug and room photos instead of the Large tier (Shift+F)">
            <FormControlLabel
              sx={{ ml: 0.5, mr: 0 }}
              control={<Switch size="small" checked={fullSources} onChange={toggleFullSources} disabled={!cur} />}
              label={<Typography variant="body2">Full-size rug and room</Typography>}
            />
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Keyboard shortcuts">
            <IconButton size="small" onClick={() => setShowKeys((v) => !v)} color={showKeys ? 'primary' : 'default'}><MdKeyboard /></IconButton>
          </Tooltip>
        </Stack>

        {showKeys && (
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'action.hover' }}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 3, rowGap: 0.5 }}>
              {KEY_HELP.map(([k, t]) => (
                <Typography key={k} variant="caption"><strong>{k}</strong> {t}</Typography>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">Shortcuts are off while you type in a field or a form is open.</Typography>
          </Box>
        )}

        {/* AI check strip (SPEC_AICHECK 5.9) */}
        {ai && (
          <Stack
            direction="row" spacing={1} alignItems="center"
            sx={{ px: { xs: 1, sm: 2 }, py: 0.5, borderBottom: `1px solid ${theme.palette.divider}`, minWidth: 0 }}
          >
            <AiChip ai={ai} />
            <Tooltip title={aiLine}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>{aiLine}</Typography>
            </Tooltip>
            <Tooltip title="The AI check's criteria, the suggested note and Check again, beside the images">
              <FormControlLabel
                sx={{ mr: 0, flex: '0 0 auto' }}
                control={<Switch size="small" checked={aiDetailsShown} onChange={(e) => setAiPref(e.target.checked)} />}
                label={<Typography variant="body2">AI details</Typography>}
              />
            </Tooltip>
          </Stack>
        )}

        {/* Body: panels (+ the open form) */}
        <Box
          sx={{
            flex: 1, minHeight: 0, display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: 1.5,
            p: { xs: 1, sm: 1.5 }, overflow: narrow && sideOpen ? 'auto' : 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1, minWidth: 0, minHeight: narrow ? (sideOpen ? 420 : 0) : 0,
              display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: 1.5,
            }}
          >
            {cur ? mode.panels.map((k) => panelFor(k)) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading || !ready ? <CircularProgress /> : <Typography color="text.secondary">This generation is not available.</Typography>}
              </Box>
            )}
          </Box>

          {sideOpen && (
            <Box
              sx={{
                width: narrow ? '100%' : (form === 'regen' ? 400 : 360), flex: '0 0 auto', minHeight: 0,
                display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: narrow ? 'visible' : 'auto',
              }}
            >
              {aiDetailsShown && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: '0 0 auto' }}>
                  <AiCheckPanel
                    ai={ai}
                    item={cur}
                    compact
                    enabled={aiEnabled !== false}
                    busy={aiBusy}
                    onCheckAgain={onAiCheck ? checkAgain : undefined}
                    onUseNote={canRegen ? openRegenWithNote : undefined}
                  />
                </Paper>
              )}
              {cur && form === 'reject' && (
                <Paper
                  variant="outlined" ref={rejectBoxRef} tabIndex={-1} onKeyDown={onRejectKeyDown}
                  sx={{ flex: '0 0 auto', p: 2, borderRadius: 2, outline: 'none' }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Reject {cur.itemId}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Pick a reason (1-{rejectReasons.length}), add a note if useful, then Enter. Esc closes.
                  </Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                    {rejectReasons.map((r, i) => {
                      const blocked = reasonBlocked(cur, r);
                      const chip = (
                        <Chip
                          key={r} clickable={!blocked} disabled={blocked}
                          label={`${i + 1} · ${REJECT_REASON_LABELS[r] || r}`}
                          color={rejectReason === r ? 'error' : 'default'}
                          variant={rejectReason === r ? 'filled' : 'outlined'}
                          onClick={blocked ? undefined : () => setRejectReason(r)}
                        />
                      );
                      return blocked ? <Tooltip key={r} title={WRONG_STYLE_TWIN_TIP}><span>{chip}</span></Tooltip> : chip;
                    })}
                  </Stack>
                  {rejectSuggested && rejectReason === rejectSuggested && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.75, mb: 1.5 }}>
                      Suggested by the AI check.
                    </Typography>
                  )}
                  {rejectReason === 'wrong_style' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Correct style (teaches the Style Sorter; regenerate afterwards to use a room of that style). Sunroom comes only from the Outdoor &amp; sunroom list.
                      </Typography>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {manualStyles(styles).filter((s) => s.code !== cur.styleCode).map((s) => {
                          const c = styleColor(s.code);
                          const on = rejectStyle === s.name;
                          return (
                            <Chip
                              key={s.code} clickable label={s.name} onClick={() => setRejectStyle(s.name)}
                              sx={{
                                fontWeight: 600, border: `1px solid ${c}66`,
                                bgcolor: on ? c : 'transparent', color: on ? '#fff' : c,
                                '&:hover': { bgcolor: on ? c : `${c}22` },
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                  <TextField
                    fullWidth size="small" label="Note (optional)" value={rejectNote} multiline minRows={2}
                    onChange={(e) => setRejectNote(e.target.value.slice(0, 1000))}
                    helperText="Enter submits, Shift+Enter adds a line."
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button onClick={() => setForm(null)} disabled={formBusy}>Cancel</Button>
                    <Button
                      variant="contained" color="error" onClick={submitReject}
                      disabled={formBusy || !rejectReason || reasonBlocked(cur, rejectReason) || (rejectReason === 'wrong_style' && !rejectStyle)}
                    >
                      {formBusy ? <CircularProgress size={20} color="inherit" /> : 'Reject'}
                    </Button>
                  </Stack>
                </Paper>
              )}

              {cur && form === 'regen' && (
                <Paper
                  variant="outlined" onKeyDown={onRegenKeyDown}
                  sx={{ flex: '0 0 auto', p: 2, borderRadius: 2 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{regenWord(cur.status)} {cur.itemId}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Attempt {cur.attempt + 1} · {shapeName(cur.shapeCode, config.shapes)} · currently {cur.roomCode}
                    {cur.status === 'unknown' ? ' · the previous attempt may already have been billed' : ''}
                  </Typography>
                  <TextField
                    fullWidth size="small" label="What should change? (required, logged)" value={regenNote} autoFocus multiline minRows={2}
                    onChange={(e) => setRegenNote(e.target.value.slice(0, 1000))}
                    error={regenNote.length > 0 && regenNote.trim().length < 3}
                    helperText={regenNote.trim().length < 3 ? 'At least 3 characters.' : 'Ctrl+Enter submits.'}
                  />
                  {ai && ai.suggestedNote && (
                    <Tooltip title={ai.suggestedNote}>
                      <Button
                        size="small" startIcon={<MdAutoFixHigh />} sx={{ mt: 0.5 }}
                        onClick={() => { setRegenNote(String(ai.suggestedNote).slice(0, 1000)); setRegenInPrompt(true); }}
                      >
                        Use the AI&apos;s note
                      </Button>
                    </Tooltip>
                  )}
                  <FormControlLabel
                    control={<Checkbox size="small" checked={regenInPrompt} onChange={(e) => setRegenInPrompt(e.target.checked)} />}
                    label={<Typography variant="body2">Add my note to the prompt</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Off: the same prompt is sent again (the note is only logged). On: your note is appended to the standard prompt.
                  </Typography>
                  <RadioGroup value={regenRoomMode} onChange={(e) => setRegenRoomMode(e.target.value)}>
                    <FormControlLabel value="same" control={<Radio size="small" />} label={<Typography variant="body2">Same room ({cur.roomCode})</Typography>} />
                    <FormControlLabel value="other" control={<Radio size="small" />} label={<Typography variant="body2">Choose the base room</Typography>} />
                  </RadioGroup>
                  {cur.targetStyleCode && cur.targetStyleCode !== cur.styleCode && (
                    <Alert severity="info" sx={{ my: 1 }}>
                      The rug&apos;s style changed to {styleName(cur.targetStyleCode, styles)} since this attempt; the least-used room of that style is preselected.
                    </Alert>
                  )}
                  {regenRoomMode === 'other' && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Click a room to choose it; click its photo to see it large.
                      </Typography>
                      <RoomPicker
                        shapeCode={cur.shapeCode}
                        styleCode={cur.targetStyleCode || cur.styleCode}
                        value={regenRoom}
                        onChange={(code, room) => { setRegenRoom(code); setRegenRoomObj(room || null); }}
                        onLoaded={onRegenRoomsLoaded}
                        onPreview={(r) => setRoomPreview(r)}
                        styles={styles}
                        currentRoomCode={cur.roomCode}
                        allowedLetters={cur.roomLetters}
                        scene={cur.scene}
                        letterNames={config.roomLetters}
                        maxHeight={300}
                      />
                      {regenWarnings.map((w) => <Alert key={w} severity="warning" sx={{ mt: 1 }}>{w} You can still use it.</Alert>)}
                    </Box>
                  )}
                  <DetailPhotoBlock
                    targetId={cur.targetId}
                    reviewReasonCode={cur.reviewReasonCode}
                    config={config}
                    value={regenDetail}
                    onChange={setRegenDetail}
                    disabled={formBusy}
                    notify={notify}
                    dense
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5 }}>Speed</Typography>
                  <SpeedChooser value={regenSpeed} onChange={setRegenSpeed} speeds={speeds} job={job} dense />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button onClick={() => setForm(null)} disabled={formBusy}>Cancel</Button>
                    <Button variant="contained" onClick={submitRegen} disabled={formBusy || !regenValid} sx={primaryButtonSx}>
                      {formBusy ? <CircularProgress size={20} color="inherit" /> : `${regenWord(cur.status)} · ${fmtRate(regenPrice)}`}
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Box>
          )}
        </Box>

        {/* Decision bar */}
        <Stack
          direction="row" spacing={1} alignItems="center"
          sx={{ px: { xs: 1, sm: 2 }, py: 1, borderTop: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Button
            variant={cur && cur.reviewState === 'approved' ? 'contained' : 'outlined'} color="success" startIcon={<MdThumbUp />}
            disabled={!canDecide || busy || formBusy || !resultShown} onClick={approve}
            title={canDecide && !resultShown ? 'Waiting for the result image to appear' : undefined}
          >
            Approve (A)
          </Button>
          <Button
            variant={cur && cur.reviewState === 'rejected' ? 'contained' : 'outlined'} color="error" startIcon={<MdThumbDown />}
            disabled={!canDecide || busy || formBusy} onClick={openReject}
          >
            Reject (R)
          </Button>
          <Button
            variant="outlined" startIcon={<MdReplay />} disabled={!canRegen || busy || formBusy} onClick={openRegen}
            sx={{ color: accent, borderColor: `${accent}88` }}
          >
            {regenWord(cur && cur.status)} (G)
          </Button>
          {canDecide && cur.reviewState !== 'pending' && (
            <Button startIcon={<MdUndo />} disabled={busy || formBusy} onClick={clearDecision}>Clear decision</Button>
          )}
          {earlierRow && (
            <Typography variant="caption" color="text.secondary">
              attempt {cur.attempt} of this rug; the current image is gen {cur.targetLatestGenId || cur.supersededByGenId || '?'} (view only)
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Previous / next skip rows that have no image yet">
            <FormControlLabel
              sx={{ mr: 0 }}
              control={<Switch size="small" checked={skipEmpty} onChange={(e) => setSkipEmpty(e.target.checked)} />}
              label={<Typography variant="body2">Skip rows without a result</Typography>}
            />
          </Tooltip>
          <Tooltip title="After Approve, Reject or Regenerate, move to the next row">
            <FormControlLabel
              sx={{ mr: 0 }}
              control={<Switch size="small" checked={nextAfter} onChange={(e) => setNextAfter(e.target.checked)} />}
              label={<Typography variant="body2">Next after a decision</Typography>}
            />
          </Tooltip>
          {cur && !earlierRow && cur.supersededByGenId ? (
            <Typography variant="caption" color="text.secondary">regenerated as gen {cur.supersededByGenId}</Typography>
          ) : null}
          {cur && (cur.note || cur.reviewNote) ? (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 520 }}>
              {cur.note ? `Regenerate note: "${cur.note}". ` : ''}
              {cur.reviewNote ? `Review note: "${cur.reviewNote}".` : ''}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      {detailOpen && cur && (
        <GenerationDetailDialog
          genId={cur.genId} onClose={() => setDetailOpen(false)} aiEnabled={aiEnabled} onAiChanged={onRefresh}
        />
      )}
      {/* A base room from the room picker, large. */}
      <ImageLightbox
        open={!!roomPreview} onClose={() => setRoomPreview(null)}
        items={roomPreview && roomPreview.base ? [{
          key: 'base', label: 'Base room', src: roomPreview.base.largeUrl || roomPreview.base.originalUrl || roomPreview.base.smallUrl,
          downloadUrl: roomPreview.base.originalUrl, downloadLabel: 'Open original',
        }] : []}
        initialKey="base" title={roomPreview ? `${roomPreview.roomCode}${roomPreview.base && roomPreview.base.imageKey ? ` · ${roomPreview.base.imageKey}` : ''}` : ''}
      />
    </Dialog>
  );
};

export default CompareDialog;
